import { useEffect, useState, useMemo } from "react";
import Sidebar from "../../components/Sidebar";
import SensorCard from "../../components/SensorCard";
import {
  useAllSensors,
  useCustomer,
} from "../../custome_hooks/sensors/useSensors";
import { useWebSocketSensor } from "../../custome_hooks/sensors/useWebSocketSensor";
import { MdOutlineSensors } from "react-icons/md";
import { FiSearch } from "react-icons/fi";
import { getSensorStatus } from "../../utils/sensorStatus";
import getApiServices from "../../services/getApiService";

/* ── Shared glass panel style ─────────────────────────────── */
const panelStyle = {
  background: "rgba(13,21,38,0.75)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.07)",
  boxShadow: "0 8px 40px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
};

const Sensor = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [allSensors, setAllSensors] = useState(true);
  const [selectedSystemId, setSelectedSystemId] = useState<string | number | null>(null);
  const [sensorList, setSensorList] = useState([]);

  const userId = localStorage?.getItem("userId");
  const { data: userData } = useCustomer(userId);
  const { data: allSensorsData, refetch } = useAllSensors(allSensors);

  /* ── Threshold map ────────────────────────────────────────── */
  const siteThresholdMap = useMemo(() => {
    const map = new Map<string, number>();
    userData?.sites?.forEach((site: any) => {
      const threshold =
        site?.criticalResistanceValue != null && !isNaN(Number(site.criticalResistanceValue))
          ? Number(site.criticalResistanceValue)
          : 3.0;
      site?.systems?.forEach((sys: any) => {
        sys?.sensors?.forEach((s: any) => {
          if (s?.id) map.set(s.id, threshold);
          if (s?.name) map.set(s.name, threshold);
        });
      });
    });
    return map;
  }, [userData]);

  /* ── Sensor enrichment ────────────────────────────────────── */
  const enrichSensors = async (sensorsArray: any[]) => {
    return await Promise.all(sensorsArray.map(async (sensor: any) => {
      let latestData = null;

      if (sensor.name) {
        try {
          const res = await getApiServices(`/be-service/sensors/latestData?deviceId=${sensor.name}`);
          if (res) latestData = res;
        } catch (err) {
          // API failed, fall back to embedded data
        }
      }

      if (!latestData && Array.isArray(sensor.data) && sensor.data.length > 0) {
        latestData = sensor.data[sensor.data.length - 1];
      }

      const rVal = latestData?.R ?? latestData?.r ?? latestData?.resistance ?? null;
      const vVal = latestData?.V ?? latestData?.v ?? latestData?.voltage ?? null;
      const iVal = latestData?.I ?? latestData?.i ?? latestData?.current ?? null;
      const gVal = latestData?.G ?? latestData?.g ?? latestData?.ground ?? null;
      const lastUpdatedAt = latestData?.timestamp || sensor.lastUpdatedAt || null;

      const criticalThreshold =
        sensor.criticalThreshold ??
        sensor.criticalResistanceValue ??
        latestData?.criticalResistanceValue ??
        siteThresholdMap.get(sensor.name) ??
        siteThresholdMap.get(sensor.id) ??
        3.0;

      const initialStatus = getSensorStatus(rVal, lastUpdatedAt, gVal, criticalThreshold);
      const isStale = initialStatus === "Inactive";

      return {
        id: sensor.id,
        name: sensor.name,
        status: initialStatus,
        criticalThreshold,
        voltage: isStale ? undefined : vVal,
        current: isStale ? undefined : iVal,
        resistance: isStale ? undefined : rVal,
        ground: isStale ? undefined : gVal,
        v: isStale ? undefined : vVal,
        i: isStale ? undefined : iVal,
        r: isStale ? undefined : rVal,
        g: isStale ? undefined : gVal,
        lastUpdatedAt: lastUpdatedAt,
        lastMaintenanceDate: sensor.maintenance?.lastMaintenanceDate,
        nextMaintenanceDate: sensor.maintenance?.nextMaintenanceDate,
      };
    }));
  };

  useEffect(() => {
    if (!allSensors) return;

    const fetchAndSet = async () => {
      const fromAll = Array.isArray(allSensorsData?.content)
        ? allSensorsData.content
        : Array.isArray(allSensorsData)
        ? allSensorsData
        : [];
      const fromUser =
        userData?.sites?.flatMap((site: any) =>
          site?.systems?.flatMap((sys: any) => sys?.sensors || [])
        ) || [];

      const combined = [...fromAll, ...fromUser].filter((s: any) => s && s.name);

      const uniqueMap = new Map();
      combined.forEach((s: any) => {
        const key = s.name || s.id;
        if (key && !uniqueMap.has(key)) uniqueMap.set(key, s);
      });
      const uniqueSensors = Array.from(uniqueMap.values());

      const enriched = await enrichSensors(uniqueSensors);
      setSensorList(enriched as any);
    };
    fetchAndSet();
  }, [allSensors, allSensorsData, userData, siteThresholdMap]);

  useWebSocketSensor(setSensorList);

  const systemList = userData?.sites?.flatMap((site: any) => site?.systems) || [];

  const filteredSensors = sensorList?.filter((s: any) => {
    const matchesSearch = s?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All" || s?.status?.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  /* ── Status summary counts ────────────────────────────────── */
  const counts = useMemo(() => ({
    total: sensorList.length,
    active: sensorList.filter((s: any) => s?.status?.toLowerCase() === "active").length,
    critical: sensorList.filter((s: any) => s?.status?.toLowerCase() === "critical").length,
    inactive: sensorList.filter((s: any) => s?.status?.toLowerCase() === "inactive").length,
  }), [sensorList]);

  return (
    <div
      className="flex flex-col min-h-[calc(100vh-64px)] p-3 sm:p-5 lg:p-6 text-white"
      style={{ background: "linear-gradient(160deg, #070c18 0%, #0a0f1e 60%, #08101f 100%)" }}
    >

      {/* ── Page Header ───────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div className="flex items-center gap-3.5">
          <div
            className="p-2.5 rounded-xl flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, rgba(59,130,246,0.18), rgba(99,102,241,0.12))",
              border: "1px solid rgba(59,130,246,0.25)",
              boxShadow: "0 4px 16px rgba(59,130,246,0.15)",
            }}
          >
            <MdOutlineSensors className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Earth Pits</h1>
            <p className="text-slate-500 text-xs sm:text-sm mt-0.5">Monitor all sensor readings and status</p>
          </div>
        </div>

        {/* Summary stat pills */}
        <div className="flex flex-wrap items-center gap-2">
          {[
            { label: "Total", count: counts.total, bg: "rgba(99,102,241,0.10)", border: "rgba(99,102,241,0.25)", color: "#818cf8" },
            { label: "Active", count: counts.active, bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.25)", color: "#34d399" },
            { label: "Critical", count: counts.critical, bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.25)", color: "#fbbf24" },
            { label: "Inactive", count: counts.inactive, bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.20)", color: "#f87171" },
          ].map(({ label, count, bg, border, color }) => (
            <div
              key={label}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: bg, border: `1px solid ${border}`, color }}
            >
              <span className="font-bold tabular-nums">{count}</span>
              <span className="opacity-80">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── 2-panel layout ────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 lg:h-[calc(100vh-196px)]">

        {/* Sidebar: horizontal on mobile, vertical column on lg+ */}
        <div className="lg:h-full lg:flex-shrink-0">
          <Sidebar
            sensors={systemList}
            selectedSystemId={selectedSystemId}
            handleSelectedSensor={async (system) => {
              setAllSensors(false);
              setSelectedSystemId(system.id);
              const enriched = await enrichSensors(system?.sensors || []);
              setSensorList(enriched as any);
            }}
          />
        </div>

        {/* Main panel */}
        <main className="flex-1 rounded-2xl overflow-hidden flex flex-col min-h-0" style={panelStyle}>

          {/* ── Top bar: title + controls ─────────────────────── */}
          <div
            className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-5 lg:px-6 py-3.5 gap-3 flex-shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            {/* Live indicator + title */}
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <h2 className="text-sm sm:text-base font-semibold text-white">
                {allSensors ? "All Earth Pits" : "Earth Pits"}
              </h2>
              <span
                className="text-[10px] font-semibold px-2 py-0.5 rounded-full tabular-nums"
                style={{
                  background: "rgba(59,130,246,0.10)",
                  border: "1px solid rgba(59,130,246,0.20)",
                  color: "#60a5fa",
                }}
              >
                {filteredSensors.length}
              </span>
            </div>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Show All toggle */}
              <label
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer text-xs font-semibold transition-all select-none"
                style={{
                  background: allSensors ? "rgba(59,130,246,0.12)" : "rgba(255,255,255,0.04)",
                  border: allSensors ? "1px solid rgba(59,130,246,0.3)" : "1px solid rgba(255,255,255,0.08)",
                  color: allSensors ? "#60a5fa" : "#94a3b8",
                }}
              >
                <input
                  type="checkbox"
                  checked={allSensors}
                  disabled={allSensors}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setAllSensors(checked);
                    setSelectedSystemId(null);
                    setSearchTerm("");
                    setStatusFilter("All");
                    if (checked) refetch();
                  }}
                  className="w-3 h-3 rounded cursor-pointer accent-blue-500"
                />
                All
              </label>

              {/* Search */}
              <div className="relative">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 w-3.5 h-3.5 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-xs pl-8 pr-3 py-1.5 rounded-full text-white placeholder-slate-500 focus:outline-none transition-all"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    minWidth: "130px",
                    boxShadow: "none",
                  }}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.border = "1px solid rgba(59,130,246,0.4)";
                    (e.target as HTMLInputElement).style.boxShadow = "0 0 0 3px rgba(59,130,246,0.12)";
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.border = "1px solid rgba(255,255,255,0.08)";
                    (e.target as HTMLInputElement).style.boxShadow = "none";
                  }}
                />
              </div>

              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-xs px-3 py-1.5 rounded-full text-white font-medium focus:outline-none cursor-pointer transition-all"
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <option value="All"    style={{ background: "#0d1526" }}>All status</option>
                <option value="Active"   style={{ background: "#0d1526" }}>Active</option>
                <option value="Critical" style={{ background: "#0d1526" }}>Critical</option>
                <option value="Inactive" style={{ background: "#0d1526" }}>Inactive</option>
              </select>
            </div>
          </div>

          {/* ── Table column headers — only visible at xl (≥ 1280px) to match SensorCard's TableRow ── */}
          <div
            className="hidden xl:flex items-center text-[10px] font-bold text-slate-500 uppercase tracking-widest px-5 xl:px-6 py-2.5 flex-shrink-0"
            style={{
              background: "rgba(0,0,0,0.15)",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <div className="w-44 shrink-0 text-left">Earth Pit</div>
            <div className="flex-1 text-center">Resistance</div>
            <div className="flex-1 text-center">Critical Limit</div>
            <div className="flex-1 text-center">Ground</div>
            <div className="flex-1 text-center">Voltage</div>
            <div className="flex-1 text-center">Current</div>
            <div className="flex-1 text-center">Status</div>
            <div className="flex-1 text-center">Maintenance</div>
          </div>

          {/* ── Sensor rows ───────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {filteredSensors.length > 0 ? (
              filteredSensors.map((sensor: any) => (
                <SensorCard key={sensor.id} sensor={sensor} />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-slate-600">
                <div
                  className="p-4 rounded-2xl mb-4"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <MdOutlineSensors className="w-10 h-10 opacity-25" />
                </div>
                <p className="text-sm font-medium text-slate-500">No earth pits found</p>
                <p className="text-xs text-slate-600 mt-1">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Sensor;
